import { useRef, useState } from 'react'
import { motion, useMotionValueEvent, useScroll } from 'framer-motion'

import Constants from '../../../app/utils/constants'
import bgVideo from '../../../assets/images/career/bgVideo.mp4'
import TitleHeader from '../../atoms/TitleHeader/TitleHeader'

const QualitiesSection = () => {
  const { heading, cards } = Constants.qualitiesSection
  const [currentStep, setCurrentStep] = useState(0)
  const [activeIndex, setActiveIndex] = useState(0)
  const sectionRef = useRef<HTMLElement | null>(null)
  const totalSteps = cards.length + 1

  const cardPositionClasses = [
    'lg:absolute left-10 top-20 md:left-0 md:top-10 lg:left-8 lg:top-8 xl:left-12 xl:top-12 2xl:top-16 2xl:left-16', // Left Top (1)
    'lg:absolute -right-20 top-10 md:-right-20 md:top-10 lg:right-2 lg:top-24 xl:right-8 xl:top-28 2xl:right-12 2xl:top-32', // Right Top (2)
    'lg:absolute left-10 top-5 md:left-0 md:top-4 lg:left-0 lg:top-[42%] xl:left-4 xl:top-[45%] 2xl:left-8 2xl:top-[48%] lg:-translate-y-1/2', // Left Middle (3)
    'lg:absolute -right-20 bottom-5 md:-right-20 md:bottom-4 lg:right-2 lg:top-[50%] xl:right-8 xl:top-[52%] 2xl:right-12 2xl:top-[54%] lg:-translate-y-1/2', // Right Middle (4)
    'lg:absolute left-10 bottom-10 md:left-0 md:bottom-10 lg:left-8 lg:bottom-12 xl:left-12 xl:bottom-16 2xl:left-16 2xl:bottom-20', // Left Bottom (5)
    'lg:absolute -right-20 bottom-20 d:-right-20 md:bottom-10 lg:right-12 lg:bottom-8 xl:right-20 xl:bottom-12 2xl:right-24 2xl:bottom-16', // Right Bottom (6)
  ] as const

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  })

  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    const nextStep = Math.min(totalSteps - 1, Math.max(0, Math.floor(latest * totalSteps)))
    const nextActiveIndex = nextStep === 0 ? 0 : Math.min(cards.length - 1, nextStep - 1)

    setCurrentStep((prev) => (prev === nextStep ? prev : nextStep))
    setActiveIndex((prev) => (prev === nextActiveIndex ? prev : nextActiveIndex))
  })

  return (
    <section ref={sectionRef} className="mx-auto">
      <div className="relative z-30 mb-16 mt-6 flex items-center justify-center px-4 text-center md:mb-20 md:mt-8 lg:mb-24">
        <TitleHeader
          badgeText="QUALITIES"
          titleLines={[heading]}
          wrapperClassName="flex w-full max-w-4xl flex-col items-center text-center"
          badgeClassName="px-10 py-3 text-xl md:px-12 md:py-4 md:text-3xl min-[1440px]:px-14 min-[1440px]:text-[2rem]"
          titleClassName="text-4xl font-rajdhani font-semibold text-center md:text-5xl min-[1440px]:text-7xl"
        />
      </div>

      <div className="relative h-[260vh]">
        <div className="sticky top-0 grid h-screen gap-6 overflow-hidden min-h-[150px] md:min-h-[450px] lg:min-h-[550px] xl:min-h-[700px] 2xl:min-h-[760px]">
          <video
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            <source src={bgVideo} type="video/mp4" />
          </video>
          <div className="pointer-events-none absolute inset-0 bg-[#09061f]/55 backdrop-blur-[2px]" />

          <div className="relative z-10 grid gap-2 md:gap-6 min-w-[50%] md:min-w-[80%] lg:block h-full md:h-[99%] lg:min-w-[90%] xl:min-w-[85%] 2xl:h-full 2xl:min-w-[65%] mx-auto">
            {cards.map((card, index) => {
              const revealCount = Math.min(cards.length, Math.max(1, activeIndex + 1))
              const isVisible = index < revealCount
              const isActive = index === activeIndex
              const isEnlargeStep = currentStep > 0

              return (
                <motion.article
                  key={`quality-slot-${index}`}
                  className={`relative w-[70%] md:w-[85%] lg:w-[48%] xl:w-[46%] 2xl:w-[36%] ${cardPositionClasses[index]}`}
                  initial={false}
                  animate={
                    isVisible
                      ? {
                          opacity: 1,
                          y: 0,
                          filter: isActive ? 'blur(0px)' : 'blur(2.8px)',
                          scale: isActive && isEnlargeStep ? 1.2 : 1.05,
                          zIndex: isActive ? 30 : 10,
                        }
                      : { opacity: 0.45, y: 0, filter: 'blur(3.2px)', scale: 0.98 }
                  }
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                  <div className="relative min-h-[130px] md:w-full overflow-hidden rounded-[22px] border border-white/18 bg-[#0a122c]/52 p-4 backdrop-blur-[8px] md:min-h-[125px] md:px-5 md:py-3 lg:min-h-[168px] lg:px-7 lg:py-4 xl:min-h-[188px] xl:px-8 xl:py-4 2xl:min-h-[198px] 2xl:px-7 2xl:py-4">
                    <div className="mb-4 flex items-center gap-3">
                      <span className="font-gilroy-bold text-lg text-white md:text-xl">
                        *{String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="h-px flex-1 bg-gradient-to-r from-white/70 via-white/35 to-transparent" />
                    </div>
                    <p className="relative z-10 max-w-[96%] pt-1 font-gilroy-medium text-base leading-[1.35] text-white md:text-base lg:max-w-[94%] lg:text-[1.08rem] lg:leading-[1.48] xl:max-w-[94%] xl:text-[1.2rem] xl:leading-[1.52] 2xl:max-w-[92%] 2xl:text-xl 2xl:leading-relaxed">
                      {card.description}
                    </p>
                  </div>
                </motion.article>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

export default QualitiesSection
